import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type CapacityPlanResult } from '@whinerp/client';
import { formatMoney } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Capacity and coverage (G-148), for the period the forecast is on.
 *
 * Who is short of pipeline for the quota they still have to find, which
 * territories have nobody working them, and how many people the quota needs
 * at what a person actually brings in.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function CapacityPanel({
  periodStart,
  periodEnd,
  periodLabel,
}: {
  periodStart: string;
  periodEnd: string;
  periodLabel: string;
}): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const [wanted, setWanted] = useState(3);
  const plan = useQuery<CapacityPlanResult, ApiError>({
    queryKey: ['capacity', periodStart, periodEnd, wanted],
    queryFn: () => api.capacityPlan({ query: { periodStart, periodEnd, coverage: wanted } }),
    placeholderData: (previous) => previous,
  });
  if (plan.isPending) return null;
  if (plan.isError) {
    return (
      <Panel data-region="capacity">
        <p className="text-dense text-danger-text">{errorText(plan.error)}</p>
      </Panel>
    );
  }
  const view = plan.data;
  const money = (amount: string) => formatMoney(amount, format, { currency: view.currency });
  const times = (value: number) => `${value.toFixed(1)}×`;

  return (
    <Panel data-region="capacity">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <SectionLabel>{t('capacity.title', { values: { period: periodLabel } })}</SectionLabel>
          <label className="ms-auto flex items-center gap-2 text-meta text-ink-meta">
            {t('capacity.wanted')}
            <select className={INPUT} data-capacity-wanted value={wanted} onChange={(event) => setWanted(Number(event.target.value))}>
              {[2, 3, 4].map((value) => <option key={value} value={value}>{times(value)}</option>)}
            </select>
          </label>
        </div>

        <table className="w-full text-dense" data-capacity-people>
          <thead>
            <tr className="text-meta text-ink-meta">
              <th className="p-1 text-start font-normal">{t('capacity.person')}</th>
              <th className="p-1 text-end font-normal">{t('capacity.remaining')}</th>
              <th className="p-1 text-end font-normal">{t('capacity.pipeline')}</th>
              <th className="p-1 text-end font-normal">{t('capacity.coverage')}</th>
            </tr>
          </thead>
          <tbody>
            {view.people.map((row) => (
              <tr key={row.userId} data-capacity-person={row.userId} data-short={row.short}>
                <td className="p-1 text-ink">{row.name}</td>
                <td className="p-1 text-end">{row.remaining === null ? t('capacity.noTarget') : money(row.remaining)}</td>
                <td className="p-1 text-end">{money(row.openPipeline)}</td>
                <td className={`p-1 text-end ${row.short ? 'text-warning-text' : 'text-ink'}`} data-capacity-coverage>
                  {row.coverage === null ? '-' : times(row.coverage)}
                  {row.short ? ` ${t('capacity.short')}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {view.territories.length > 0 ? (
          <ul className="flex flex-col gap-1 text-dense" data-capacity-territories>
            {view.territories.map((row) => (
              <li key={row.territoryId} data-capacity-territory={row.territoryId} className="flex flex-wrap gap-2">
                <span className="text-ink">{row.name}</span>
                {row.uncovered ? (
                  <span className="text-warning-text" data-territory-uncovered>
                    {t('capacity.uncovered', { values: { accounts: String(row.accounts) } })}
                  </span>
                ) : (
                  <span className="text-ink-meta">
                    {t('capacity.territory', {
                      values: { accounts: String(row.accounts), people: String(row.people), each: String(row.accountsPerPerson ?? 0) },
                    })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : null}

        <p className="text-dense text-ink" data-capacity-headcount>
          {view.headcount.needed === null
            ? t('capacity.headcountUnknown', { values: { quarters: String(view.headcount.quartersOfHistory) } })
            : t('capacity.headcount', {
                values: {
                  needed: String(view.headcount.needed),
                  have: String(view.headcount.have),
                  target: money(view.headcount.teamTarget),
                  each: money(view.headcount.perPersonPerQuarter ?? '0'),
                },
              })}
        </p>
        {view.otherCurrency > 0 ? (
          <p className="text-meta text-ink-meta">{t('capacity.otherCurrency', { values: { count: String(view.otherCurrency), currency: view.currency } })}</p>
        ) : null}
      </div>
    </Panel>
  );
}
